import { formatCompactTokens } from "../chat/chat-context-usage.ts";
import type { GatewayBrowserClient } from "../gateway.ts";
import { SESSION_KEYED_CACHE_MAX_SESSIONS, rememberSessionKeyedEntry } from "./session-uploads.ts";

export type ContextBreakdownCategoryId =
  | "systemPrompt"
  | "tools"
  | "rules"
  | "skills"
  | "mcp"
  | "subagents"
  | "summarizedConversation"
  | "conversation";

export type SessionContextBreakdown = {
  sessionKey: string;
  usedTokens: number;
  limitTokens: number | null;
  percent: number | null;
  model: string | null;
  reportSource: "run" | "estimate";
  reportGeneratedAt: number;
  categories: Array<{ id: ContextBreakdownCategoryId; label: string; tokens: number }>;
  subagentSessions: Array<{ sessionKey: string; label: string | null; tokens: number }>;
};

export type ChatContextBreakdownState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  sessionKey: string;
  chatContextPanelOpen: boolean;
  chatContextBreakdownLoading: boolean;
  chatContextBreakdownError: string | null;
  chatContextBreakdown: SessionContextBreakdown | null;
  /**
   * JAISIU-3307 (Pillar B) — write-through cache keyed by trimmed
   * session key. The app shell mirrors `chatContextBreakdown` into it
   * on every successful fetch; `activateChatSession` consults it so
   * the inspector paints the previous session's snapshot
   * synchronously. Optional: legacy callers without the slot still
   * hit the existing success / failure paths unchanged.
   */
  chatContextBreakdownBySession?: Map<string, SessionContextBreakdown>;
};

function isUnknownContextBreakdownMethod(err: unknown): boolean {
  return String(err).includes("unknown method: sessions.contextBreakdown");
}

export function formatChatContextBreakdownError(err: unknown): string {
  if (isUnknownContextBreakdownMethod(err)) {
    return "Context breakdown needs a newer gateway. Run pnpm build, restart the gateway, and refresh.";
  }
  return String(err);
}

export function formatBreakdownTokens(n: number): string {
  return formatCompactTokens(n);
}

export async function loadChatContextBreakdown(state: ChatContextBreakdownState) {
  if (!state.client || !state.connected || !state.sessionKey.trim()) {
    state.chatContextBreakdown = null;
    state.chatContextBreakdownError = null;
    return;
  }
  const requestedKey = state.sessionKey.trim();
  state.chatContextBreakdownLoading = true;
  state.chatContextBreakdownError = null;
  try {
    const res = await state.client.request<{ breakdown: SessionContextBreakdown }>(
      "sessions.contextBreakdown",
      { key: requestedKey },
    );
    const next = res?.breakdown ?? null;
    // JAISIU-3307 — gate the write by the session key still matching.
    // Otherwise a fetch that started for chat-A could paint onto chat-B
    // when the operator switched tabs while the network was in flight.
    if (state.sessionKey.trim() === requestedKey) {
      state.chatContextBreakdown = next;
    }
    if (next && state.chatContextBreakdownBySession) {
      // Bounded: see the note on SESSION_KEYED_CACHE_MAX_SESSIONS.
      rememberSessionKeyedEntry(
        state.chatContextBreakdownBySession,
        requestedKey,
        next,
        SESSION_KEYED_CACHE_MAX_SESSIONS,
      );
    }
  } catch (err) {
    if (state.sessionKey.trim() === requestedKey) {
      state.chatContextBreakdownError = formatChatContextBreakdownError(err);
      // Keep the previous snapshot so the inspector doesn't blank on a
      // transient explain failure — the operator can manually retry.
      // The legacy behaviour was to null out the active breakdown on
      // error, which Pillar B explicitly wants gone.
    }
  } finally {
    if (state.sessionKey.trim() === requestedKey) {
      state.chatContextBreakdownLoading = false;
    }
  }
}

export function openChatContextPanel(state: ChatContextBreakdownState) {
  state.chatContextPanelOpen = true;
  void loadChatContextBreakdown(state);
}

export function closeChatContextPanel(state: ChatContextBreakdownState) {
  state.chatContextPanelOpen = false;
}
